import React from 'react'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { cm } from '~/utils/utils'
import { ChevronLeftIcon, ChevronRightIcon } from './icons'
import type { LeaderboardEntry, PaginationData } from './types'

interface LeaderboardTableProps {
  data: LeaderboardEntry[]
  pagination: PaginationData
  onPageChange?: (page: number) => void
  className?: string
  isBillableFlowPlan?: boolean
}

interface RankBadgeProps {
  rank: number
}

const RankBadge: React.FC<RankBadgeProps> = ({ rank }) => {
  const isTopThree = rank <= 3

  return (
    <div
      className={cm(
        "w-[24px] h-[24px] rounded-full flex items-center justify-center font-['Geist',sans-serif] text-[12px] font-semibold",
        isTopThree ? 'bg-[#9D7EE6] text-white' : 'bg-transparent text-[rgba(0,0,0,0.88)]'
      )}
    >
      {rank}
    </div>
  )
}

export const LeaderboardTable: React.FC<LeaderboardTableProps> = ({
  data,
  pagination,
  onPageChange,
  className,
  isBillableFlowPlan = false
}) => {
  const isCompactViewport = useCompactViewport()

  const handlePrevPage = () => {
    if (pagination.current_page > 1) {
      onPageChange?.(pagination.current_page - 1)
    }
  }

  const handleNextPage = () => {
    if (pagination.current_page < pagination.total_pages) {
      onPageChange?.(pagination.current_page + 1)
    }
  }

  return (
    <div
      className={cm(
        'bg-[rgba(255,255,255,0.5)] border border-white rounded-[24px] flex flex-col',
        !className?.includes('w-') && 'w-[782px]',
        !className?.includes('h-') && (isCompactViewport ? 'h-auto' : 'h-[622px]'),
        className
      )}
    >
      {/* Title */}
      <div className={cm(isCompactViewport ? 'pt-4 px-4' : 'pt-[24px] pl-[36px]')}>
        <p className="font-['Geist',sans-serif] text-[14px] font-bold leading-[18px] text-[#2E1F36]">
          Leaderboard
        </p>
      </div>

      {/* Table */}
      <div className='w-full flex-1 overflow-x-auto'>
        <div
          className={cm(
            'flex flex-col gap-[4px]',
            isCompactViewport ? 'min-w-[560px] px-4 py-3' : 'w-full px-[24px] py-[16px]'
          )}
        >
          {/* Header Row */}
          <div className='w-full h-[40px] flex items-center gap-[24px] px-[16px]'>
            <div className='w-[32px]'>
              <p className="font-['Geist_Mono',monospace] text-[12px] font-semibold text-[rgba(0,0,0,0.65)]">
                Rank
              </p>
            </div>
            <div className='w-[120px]'>
              <p className="font-['Geist_Mono',monospace] text-[12px] font-semibold text-[rgba(0,0,0,0.65)]">
                Developer
              </p>
            </div>
            <div className='flex-1 text-right'>
              <p className="font-['Geist_Mono',monospace] text-[12px] font-semibold text-[rgba(0,0,0,0.65)]">
                Messages
              </p>
            </div>
            <div className='flex-1 text-right'>
              <p className="font-['Geist_Mono',monospace] text-[12px] font-semibold text-[rgba(0,0,0,0.65)]">
                Tasks
              </p>
            </div>
            <div className='flex-1 text-right'>
              <p className="font-['Geist_Mono',monospace] text-[12px] font-semibold text-[rgba(0,0,0,0.65)]">
                {isBillableFlowPlan ? 'Billable Flows' : 'Credit Usage'}
              </p>
            </div>
          </div>

          {/* Data Rows */}
          <div className='flex flex-col'>
            {data.map((row, index) => {
              const rank = (pagination.current_page - 1) * 10 + index + 1

              return (
                <div
                  key={row.id}
                  className='w-full flex items-center gap-[24px] px-[16px] py-[8px] rounded-[12px] select-text hover:bg-[rgba(0,0,0,0.02)]'
                >
                  {/* Rank */}
                  <div className='w-[32px] flex items-center justify-center'>
                    <RankBadge rank={rank} />
                  </div>

                  {/* Developer */}
                  <div className='w-[120px] flex items-center'>
                    <p className="font-['Geist',sans-serif] text-[12px] font-medium leading-[16px] text-[rgba(0,0,0,0.88)] truncate">
                      {row.name}
                    </p>
                  </div>

                  {/* Messages */}
                  <div className='flex-1 text-right'>
                    <p className="font-['Geist',sans-serif] text-[12px] font-medium leading-[16px] text-[rgba(0,0,0,0.88)]">
                      {row.messages.toLocaleString()}
                    </p>
                  </div>

                  {/* Tasks */}
                  <div className='flex-1 text-right'>
                    <p className="font-['Geist',sans-serif] text-[12px] font-medium leading-[16px] text-[rgba(0,0,0,0.88)]">
                      {(row.tasks ?? 0).toLocaleString()}
                    </p>
                  </div>

                  {/* Credit Usage (super admin only) / Billable Flows */}
                  <div className='flex-1 text-right'>
                    <p className="font-['Geist',sans-serif] text-[12px] font-medium leading-[16px] text-[rgba(0,0,0,0.88)]">
                      {isBillableFlowPlan
                        ? (row.billable_flows ?? 0).toLocaleString()
                        : row.credit_used.toLocaleString()}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Pagination Footer */}
      <div className='w-full h-[56px] flex justify-center items-center border-t border-white'>
        <div className='flex items-center gap-[8px]'>
          <button
            onClick={handlePrevPage}
            disabled={pagination.current_page === 1}
            className='w-[24px] h-[24px] flex items-center justify-center cursor-pointer disabled:cursor-not-allowed'
            style={{
              color: pagination.current_page === 1 ? 'rgba(0, 0, 0, 0.25)' : 'rgba(0,0,0,0.88)'
            }}
          >
            <ChevronLeftIcon />
          </button>

          <div className='flex items-center'>
            <span className="font-['Geist',sans-serif] text-[14px] font-medium leading-[16px] text-[rgba(0,0,0,0.88)] w-[24px] h-[24px] flex items-center justify-center">
              {pagination.current_page}
            </span>
            <span className="font-['Geist',sans-serif] text-[14px] font-medium leading-[16px] text-[rgba(0,0,0,0.45)] w-[24px] h-[24px] flex items-center justify-center">
              /
            </span>
            <span className="font-['Geist',sans-serif] text-[14px] font-medium leading-[16px] text-[rgba(0,0,0,0.45)] w-[24px] h-[24px] flex items-center justify-center">
              {pagination.total_pages}
            </span>
          </div>

          <button
            onClick={handleNextPage}
            disabled={pagination.current_page === pagination.total_pages}
            className='w-[24px] h-[24px] flex items-center justify-center cursor-pointer disabled:cursor-not-allowed'
            style={{
              color:
                pagination.current_page === pagination.total_pages
                  ? 'rgba(0, 0, 0, 0.25)'
                  : 'rgba(0,0,0,0.88)'
            }}
          >
            <ChevronRightIcon />
          </button>
        </div>
      </div>
    </div>
  )
}

export default LeaderboardTable
